/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import * as Yup from "yup";

import { IPersonaProps, Stack, StackItem } from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";

import ComboBoxController from "Components/ReactHookForm/ComboBoxController/ComboBoxController";
import DatePickerController from "Components/ReactHookForm/DatePickerController/DatePickerController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { spanStyles } from "Components/StudentProgramProfile/StudentProgramProfile.styles";
import { OperationStatus } from "Constants/OperationStatus";
import { milestones, MlsaProgramProfileStatuses } from "Constants/ProfileConstants";
import { fetchProgramProfile, updateProgramProfile } from "Dataflows/UserProfile/UserProfileAction";
import {
  getProgramProfileOperationStatus,
  getProgramProfileUpdateErrorMessage,
} from "Dataflows/UserProfile/UserProfileSelector";

import { IStudentProgramProfileEditFormProps } from "./IStudentProgramProfileEditFormProps";
import PeoplePickerController from "Components/ReactHookForm/PeoplePickerController/PeoplePickerController";
import { IAssignee } from "Interfaces/IAssignee";
import { IProgramProfile } from "Interfaces/ProgramProfile/IProgramProfile";

export const StudentProgramProfileEditForm = (
  props: IStudentProgramProfileEditFormProps
): ReactElement => {
  const { isPanelOpen, setIsPanelOpen, defaultValues, shouldAutoAdvanceLevel } = props;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(
      Yup.object().shape({
        programProfileStatusName: Yup.string().required("Program Profile Status is required"),
        programEndDate: Yup.date().required("Program end date is required"),
        levelStatusName: Yup.string().required("Milestone is required"),
        cpm: Yup.string().required("CPM is required").nullable(),
      })
    ),
  });

  const dispatch = useDispatch();

  const dismissPanel = (): void => {
    setIsPanelOpen(false);
    reset();
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const save = () =>
    handleSubmit((data) => {
      return dispatch(updateProgramProfile(data as IProgramProfile));
    });

  const programProfileOperationStatus = useSelector(getProgramProfileOperationStatus);
  const programProfileErrorMessage = useSelector(getProgramProfileUpdateErrorMessage);

  useEffect(() => {
    if (programProfileOperationStatus.update === OperationStatus.Succeeded) {
      dispatch(fetchProgramProfile(defaultValues.userProfileId as number));
      dismissPanel();
    }
  }, [programProfileOperationStatus.update === OperationStatus.Succeeded]);

  const isSaving = programProfileOperationStatus.update === OperationStatus.Pending;

  const mapAssigneesToOptions = (assignees: IAssignee[]): IPersonaProps[] => {
    return assignees.map((assignee) => {
      return {
        text: `${assignee.firstName} ${assignee.lastName}`,
        secondaryText: assignee.emailAddress,
      };
    });
  };

  return (
    <form onSubmit={save()}>
      <SidePanel
        isOpen={isPanelOpen}
        title="Edit Program Details"
        saveButtonLabel="Save"
        error={programProfileErrorMessage}
        disableActions={isSaving}
        onDismiss={dismissPanel}
        onSave={save()}
      >
        <Stack tokens={{ childrenGap: "1rem" }}>
          <StackItem>
            <ComboBoxController
              control={control}
              errors={errors}
              inputName="programProfileStatusName"
              isRequired={true}
              labelDisplayName="Program Profile Status"
              options={MlsaProgramProfileStatuses}
              isDisabled={isSaving}
            />
          </StackItem>
          {defaultValues.availableCommunityProgramManagers && (
            <StackItem>
              <PeoplePickerController
                control={control}
                inputName="cpm"
                errors={errors}
                labelDisplayName="CPM"
                isDisabled={false}
                options={mapAssigneesToOptions(defaultValues.availableCommunityProgramManagers)}
                keyPropertyName="secondaryText"
                defaultSelectedKey={defaultValues.cpm}
                isRequired={true}
                hasTooltip={true}
                toolTipDisplayText="Select the Community Program Manager"
              />
            </StackItem>
          )}
          <StackItem>
            <DatePickerController
              control={control}
              errors={errors}
              inputName="programEndDate"
              isRequired={true}
              labelDisplayName="Program End Date"
              startDate={defaultValues.programEndDate}
              isDisabled={isSaving}
            />
          </StackItem>
          <StackItem>
            <ComboBoxController
              control={control}
              errors={errors}
              inputName="levelStatusName"
              isRequired={true}
              labelDisplayName="Milestone"
              options={milestones}
              isDisabled={isSaving}
            />
          </StackItem>
          {!shouldAutoAdvanceLevel && (
            <StackItem style={spanStyles}>Auto Advance Disabled</StackItem>
          )}
        </Stack>
      </SidePanel>
    </form>
  );
};
